import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Route, Switch, useLocation, useParams, useRouteMatch, Redirect } from 'react-router-dom';
import { api } from '../services/api';
import OpsOrgSummary from './OpsOrgSummary';
import OpsIdentityVerification from './OpsIdentityVerification';
import OpsOrgUsers from './OpsOrgUsers';
import OpsOrgAudit from './OpsOrgAudit';
import OpsOrgApiKeys from './OpsOrgApiKeys';
import OpsOrgApiKeyUsage from './OpsOrgApiKeyUsage';
import OpsOrgApiEndpointAnalytics from './OpsOrgApiEndpointAnalytics';
import OpsOrgInvites from './OpsOrgInvites';
import OpsOrgActions from './OpsOrgActions';
import OpsSupportAudit from './OpsSupportAudit';
import OpsOrgLoginAttempts from './OpsOrgLoginAttempts';
import OpsOrgTrialSupport from './OpsOrgTrialSupport';

const formatSubscriptionBadge = (subscription) => {
  if (!subscription || subscription.is_internal) return 'Subscription: Internal';
  if (subscription.plan_code === 'trial' || subscription.status === 'trialing') return 'Subscription: Trial';
  if (!subscription.plan_name) return 'Subscription: None';
  return `Subscription: ${subscription.plan_name}`;
};

const OpsOrgShell = () => {
  const { orgId } = useParams();
  const location = useLocation();
  const { path, url } = useRouteMatch();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshNonce, setRefreshNonce] = useState(0);

  const loadSummary = async (mountedRef = { current: true }) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/summary`);
      if (!mountedRef.current) return;
      setSummary(response.data);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err.response?.data?.error || 'Failed to load organization summary');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    const mountedRef = { current: true };
    if (orgId) loadSummary(mountedRef);
    return () => {
      mountedRef.current = false;
    };
  }, [orgId]);

  const showTrialSupport = ['trialing', 'expired'].includes(summary?.subscription?.status)
    && summary?.subscription?.plan_code === 'trial';
  const activeSection = useMemo(() => {
    const pathName = location.pathname || '';
    if (pathName.includes('/support-audit')) return 'support-audit';
    if (pathName.includes('/trial-support')) return 'trial-support';
    if (pathName.includes('/actions')) return 'actions';
    if (pathName.includes('/login-attempts')) return 'login-attempts';
    if (pathName.includes('/invites')) return 'invites';
    if (pathName.includes('/api-endpoint-analytics')) return 'api-endpoint-analytics';
    if (pathName.includes('/api-key-usage')) return 'api-key-usage';
    if (pathName.includes('/api-keys')) return 'api-keys';
    if (pathName.includes('/audit')) return 'audit';
    if (pathName.includes('/users')) return 'users';
    return 'summary';
  }, [location.pathname]);
  const refreshDisabled = activeSection === 'actions';
  const handleRefresh = () => {
    if (refreshDisabled) return;
    loadSummary();
    setRefreshNonce((value) => value + 1);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
            <h2 className="h4 fw-bold mb-0">{summary?.org_name || 'Organization Support'}</h2>
            <span className="badge bg-light text-dark border">
              {`Org ID: ${summary?.org_id ?? '—'}`}
            </span>
            <span className="badge bg-light text-dark border">
              {formatSubscriptionBadge(summary?.subscription)}
            </span>
          </div>
          <p className="text-muted mb-0">Support tools for this organization.</p>
        </div>
        <NavLink className="btn btn-outline-primary btn-sm" to="/admin">
          Change Organization
        </NavLink>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h5 className="card-title mb-2">Support Tools</h5>
              <div className="d-flex flex-wrap gap-2">
                <NavLink className="btn btn-outline-primary btn-sm" to={`${url}/summary`} activeClassName="active">
                  Summary
                </NavLink>
                <NavLink className="btn btn-outline-primary btn-sm" to={`${url}/users`} activeClassName="active">
                  Users
                </NavLink>
                <NavLink className="btn btn-outline-primary btn-sm" to={`${url}/audit`} activeClassName="active">
                  Audit History
                </NavLink>
                <NavLink className="btn btn-outline-primary btn-sm" to={`${url}/api-keys`} activeClassName="active">
                  API Keys
                </NavLink>
                <NavLink className="btn btn-outline-primary btn-sm" to={`${url}/api-key-usage`} activeClassName="active">
                  API Key Utilization
                </NavLink>
                <NavLink className="btn btn-outline-primary btn-sm" to={`${url}/invites`} activeClassName="active">
                  Invites
                </NavLink>
                <NavLink className="btn btn-outline-primary btn-sm" to={`${url}/login-attempts`} activeClassName="active">
                  Login Attempts
                </NavLink>
                <NavLink className="btn btn-outline-warning btn-sm" to={`${url}/api-endpoint-analytics`} activeClassName="active">
                  API Endpoint Analytics
                </NavLink>
                <NavLink className="btn btn-outline-warning btn-sm" to={`${url}/actions`} activeClassName="active">
                  Support Actions
                </NavLink>
                {showTrialSupport && (
                  <NavLink className="btn btn-outline-warning btn-sm" to={`${url}/trial-support`} activeClassName="active">
                    Trial Support
                  </NavLink>
                )}
                <NavLink className="btn btn-outline-warning btn-sm" to={`${url}/support-audit`} activeClassName="active">
                  Support Audit Trail
                </NavLink>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handleRefresh}
              disabled={loading || refreshDisabled}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      <Switch>
        <Route exact path={path}>
          <Redirect to={`${url}/summary`} />
        </Route>
        <Route path={`${path}/summary`}>
          <OpsOrgSummary key={`summary-${refreshNonce}`} summary={summary} loading={loading} error={error} onRefresh={() => loadSummary()} />
        </Route>
        <Route path={`${path}/users`}>
          <OpsOrgUsers key={`users-${refreshNonce}`} />
        </Route>
        <Route path={`${path}/audit`}>
          <OpsOrgAudit key={`audit-${refreshNonce}`} />
        </Route>
        <Route path={`${path}/api-keys`}>
          <OpsOrgApiKeys key={`api-keys-${refreshNonce}`} />
        </Route>
        <Route path={`${path}/actions`}>
          <OpsOrgActions summary={summary} />
        </Route>
        <Route path={`${path}/trial-support`}>
          {showTrialSupport ? (
            <OpsOrgTrialSupport key={`trial-support-${refreshNonce}`} summary={summary} loading={loading} error={error} onRefresh={() => loadSummary()} />
          ) : (
            <Redirect to={`${url}/summary`} />
          )}
        </Route>
        <Route path={`${path}/support-audit`}>
          <OpsSupportAudit key={`support-audit-${refreshNonce}`} />
        </Route>
        <Route path={`${path}/api-key-usage`}>
          <OpsOrgApiKeyUsage key={`api-key-usage-${refreshNonce}`} />
        </Route>
        <Route path={`${path}/api-endpoint-analytics`}>
          <OpsOrgApiEndpointAnalytics key={`api-endpoint-analytics-${refreshNonce}`} />
        </Route>
        <Route path={`${path}/invites`}>
          <OpsOrgInvites key={`invites-${refreshNonce}`} />
        </Route>
        <Route path={`${path}/login-attempts`}>
          <OpsOrgLoginAttempts key={`login-attempts-${refreshNonce}`} />
        </Route>
      </Switch>
    </div>
  );
};

export default OpsOrgShell;
